/**
 * Registry of "insert content" handlers for open editors.
 *
 * Each editor panel registers a handler while mounted (mounted === its
 * sidebar is open). The chat's text-selection popup uses the active handler
 * to offer "Add to document/spreadsheet/presentation" for highlighted chat
 * content.
 *
 * Leaf module: no store/api imports, safe to require from anywhere.
 */
const inserters = new Map();
const listeners = new Set();

function notify() {
  for (const listener of listeners) listener();
}

/**
 * @param {string} key stable per-editor key, e.g. 'document-editor'
 * @param {{ noun: string, insert: (text: string) => void }} inserter
 *   `noun` is the label suffix ("document" → "Add to document").
 * @returns {() => void} unregister
 */
export function registerEditorInsert(key, inserter) {
  const previous = inserters.get(key);
  inserters.set(key, inserter);
  // Panels re-register whenever their insert callback changes identity, which
  // happens on every edit. Only a new editor opening or closing changes what
  // subscribers render, so don't wake them for a same-noun swap.
  if (previous?.noun !== inserter?.noun) notify();
  return () => {
    if (inserters.get(key) === inserter) {
      inserters.delete(key);
      notify();
    }
  };
}

/** The most recently registered inserter, or null when no editor is open. */
export function getActiveEditorInsert() {
  let latest = null;
  for (const inserter of inserters.values()) latest = inserter;
  return latest;
}

/**
 * Noun of the open editor, or null when none is open. A primitive snapshot so
 * subscribers only re-render when the target actually changes.
 */
export function getActiveEditorNoun() {
  return getActiveEditorInsert()?.noun ?? null;
}

/** Subscribe to editors opening/closing. @returns {() => void} unsubscribe */
export function subscribeEditorInsert(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}
